import api from "./api";
import { enderecoService } from "./enderecoService";
import axios, { AxiosResponse } from "axios";
import {
  Empreendimento,
  EmpreendimentoCreate,
  EmpreendimentoUpdate,
  PaginatedEmpreendimentoResponse,
  ParteEmpreendimento,
  ParteEmpreendimentoCreate,
  TipoEmpreendimento,
  TipoEmpreendimentoCreate,
  ItemEmpreendimento,
  ItemEmpreendimentoCreate,
  TipoParteEmpreendimento,
  TipoParteEmpreendimentoCreate,
  PaginatedResponse,
} from "../types/empreendimento";

// O "next" da paginação do DRF vem como URL absoluta e pode chegar com host ou
// protocolo diferentes do baseURL (ex.: http:// quando a API está atrás de um
// proxy). Nesse caso o replace do baseURL não casava, o axios requisitava a URL
// absoluta e o browser bloqueava por mixed content/CORS. Aproveitamos só o path
// + query e deixamos o axios resolver contra o baseURL.
const toRelativeUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  try {
    const parsed = new URL(url, window.location.origin);
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return url;
  }
};

export class EmpreendimentoService {
  private baseUrl = "/api/empreendimentos/";

  async list(params?: {
    limit?: number;
    offset?: number;
    search?: string;
    incorporadora?: number;
    cidade?: number;
  }): Promise<PaginatedEmpreendimentoResponse> {
    const response = await api.get(this.baseUrl, { params });
    return response.data;
  }

  async getById(id: number): Promise<Empreendimento> {
    try {
      const response = await api.get(`${this.baseUrl}${id}/`);
      return response.data;
    } catch (error: any) {
      console.error("Erro detalhado na busca do empreendimento:", error);
      console.error("Status:", error.response?.status);
      console.error("Data:", error.response?.data);
      console.error("Headers:", error.response?.headers);
      throw error;
    }
  }

  async get(id: number): Promise<Empreendimento> {
    return this.getById(id);
  }

  async create(empreendimento: EmpreendimentoCreate): Promise<Empreendimento> {
    try {
      const formData = new FormData();

      Object.entries(empreendimento).forEach(([key, value]) => {
        // Primeiro, pulamos qualquer valor que seja nulo ou indefinido
        if (value === undefined || value === null) {
          return;
        }

        // --- INÍCIO DA MODIFICAÇÃO ---

        // 1. Tratamento especial para o array de MÚLTIPLAS IMAGENS
        if (key === "imagens") {
          (value as File[]).forEach((file) => {
            // Verificamos se é realmente um arquivo antes de adicionar
            if (file instanceof File) {
              formData.append("imagens", file, file.name);
            }
          });

          // 2. Tratamento genérico para qualquer arquivo único (tabela_preco,
          //    book, arquivo_extra_1, arquivo_extra_2, ...): value.toString()
          //    quebraria (viraria "[object File]"), então precisa vir antes
          //    do fallback genérico abaixo.
        } else if (value instanceof File) {
          formData.append(key, value, value.name);

          // 3. Tratamento especial para OBJETOS ou ARRAYS DE OBJETOS
        } else if (key === "endereco" || key === "partes_empreendimento") {
          formData.append(key, JSON.stringify(value));

          // --- FIM DA MODIFICAÇÃO ---

          // 4. Lógica original para os outros campos (que continua funcionando bem)
        } else if (Array.isArray(value)) {
          // Para arrays de valores simples (ex: tipo_empreendimento: [1, 2])
          value.forEach((v) => formData.append(key, v.toString()));
        } else {
          // Para todos os outros valores simples (nome, incorporadora, etc.)
          formData.append(key, value.toString());
        }
      });

      // O resto da função permanece igual
      const response = await api.post(this.baseUrl, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      return response.data;
    } catch (error: any) {
      console.error("=== ERRO DA API ===");
      console.error(
        "Dados do erro:",
        JSON.stringify(error.response?.data, null, 2)
      );
      throw error;
    }
  }

  async update(
    id: number,
    empreendimento: EmpreendimentoUpdate
  ): Promise<Empreendimento> {
    const response = await api.put(`${this.baseUrl}${id}/`, empreendimento);
    return response.data;
  }

  async patch(
    id: number,
    empreendimento: Partial<EmpreendimentoUpdate>
  ): Promise<Empreendimento> {
    const response = await api.patch(`${this.baseUrl}${id}/`, empreendimento);
    return response.data;
  }

  async delete(id: number): Promise<void> {
    await api.delete(`${this.baseUrl}${id}/`);
  }

  async createWithParts(
    empreendimento: EmpreendimentoCreate,
    partes: ParteEmpreendimentoCreate[]
  ): Promise<Empreendimento> {
    try {
      // Se o endereço é um objeto, criar primeiro
      let enderecoId = empreendimento.endereco;
      if (typeof empreendimento.endereco === "object") {
        const endereco = await enderecoService.createEndereco(
          empreendimento.endereco as any
        );
        enderecoId = endereco.id;
      }

      // Preparar dados do empreendimento com o ID do endereço.
      // `partes_empreendimento` (se vier embutido em `empreendimento`, como
      // acontece hoje vindo do formulário) é removido daqui: o backend não lê
      // esse campo na criação do empreendimento, e as unidades já são criadas
      // uma a uma logo abaixo. Sem isso, empreendimentos com centenas/milhares
      // de unidades mandavam esse JSON gigante (com todas as unidades) junto
      // dos uploads (imagens, tabela de preço, book, extras) na MESMA
      // requisição — dobrando o envio das unidades à toa e estourando o
      // limite de tamanho de request (413) em empreendimentos grandes.
      const { partes_empreendimento, ...empreendimentoSemPartes } =
        empreendimento as EmpreendimentoCreate & {
          partes_empreendimento?: unknown;
        };
      const empreendimentoData = {
        ...empreendimentoSemPartes,
        endereco: enderecoId,
        heading: empreendimento.nome || "Empreendimento",
        pitch: `Empreendimento ${empreendimento.nome || ""}`,
      };

      const empreendimentoCriado = await this.create(empreendimentoData);

      for (const parte of partes) {
        try {
          // Garantir que o empreendimento_id seja enviado corretamente
          const parteComEmpreendimento = {
            ...parte,
            empreendimento: empreendimentoCriado.id,
          };

          await this.createParteEmpreendimento(parteComEmpreendimento);
        } catch (parteError: any) {
          try {
            await this.delete(empreendimentoCriado.id);
          } catch (deleteError) {
            console.error(
              "Erro ao deletar empreendimento após falha na parte:",
              deleteError
            );
          }
          throw new Error(
            `Erro ao criar parte do empreendimento: ${
              parteError.message || "Erro desconhecido"
            }`
          );
        }
      }

      return empreendimentoCriado;
    } catch (error: any) {
      console.error("Erro no createWithParts:", error);

      if (error.response) {
        throw error;
      } else if (error.message) {
        throw error;
      } else {
        throw new Error("Erro desconhecido ao criar empreendimento");
      }
    }
  }

  async patchFormData(id: number, formData: FormData): Promise<Empreendimento> {
    try {
      const response = await api.patch(`${this.baseUrl}${id}/`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (error: any) {
      console.error("=== ERRO PATCH FORM DATA ===", error.response?.data);
      throw error;
    }
  }

  async updateParteEmpreendimento(
    parteId: number,
    // Usamos Partial<T> para indicar que o payload pode ter apenas alguns campos
    partePartial: Partial<ParteEmpreendimentoCreate>
  ): Promise<ParteEmpreendimento> {
    const payload = partePartial;

    try {
      const response = await api.patch(`/api/unidade/${parteId}/`, payload);
      return response.data;
    } catch (error: any) {
      console.error(`Erro detalhado ao atualizar parte ${parteId}:`, error);
      console.error("Response data:", error.response?.data);
      console.error("Response status:", error.response?.status);
      throw new Error(
        `Erro ao atualizar parte do empreendimento: ${error.message}`
      );
    }
  }

  async createParteEmpreendimento(
    parte: ParteEmpreendimentoCreate
  ): Promise<ParteEmpreendimento> {
    // Criar payload completo com todos os campos necessários
    const payloadCompleto = {
      nome: parte.nome,
      torre: parte.torre,
      area_privativa_m2: parte.area_privativa_m2,
      valor_imovel: parte.valor_imovel,
      forma_pagamento: parte.forma_pagamento,
      disponibilidade: parte.disponibilidade,
      quantidade_garagem: parte.quantidade_garagem,
      tipo_parte_empreendimento: parte.tipo_parte_empreendimento,
      empreendimento: parte.empreendimento,

      // Campos opcionais
      ...(parte.ano_disponibilidade !== undefined && {
        ano_disponibilidade: parte.ano_disponibilidade,
      }),
      ...(parte.mes_disponibilidade !== undefined && {
        mes_disponibilidade: parte.mes_disponibilidade,
      }),
      ...(parte.origem_recurso !== undefined && {
        origem_recurso: parte.origem_recurso,
      }),
      ...(parte.atributos_extras && {
        atributos_extras: parte.atributos_extras,
      }),
      ...(parte.mcmv !== undefined && { mcmv: parte.mcmv }),
      ...(parte.tipo_produto !== undefined && {
        tipo_produto: parte.tipo_produto,
      }),
      ...(parte.andar != undefined && { andar: parte.andar }),
      ...(parte.numero_apartamento != undefined && {
        numero_apartamento: parte.numero_apartamento,
      }),
      ...(parte.vagas_rotativas != undefined && {
        vagas_rotativas: parte.vagas_rotativas,
      }),
      ...(parte.rotativa_fixa != undefined && {
        rotativa_fixa: parte.rotativa_fixa,
      }),
      ...(parte.rotativa_aleatoria != undefined && {
        rotativa_aleatoria: parte.rotativa_aleatoria,
      }),
      ...(parte.valor_avaliacao != undefined && {
        valor_avaliacao: parte.valor_avaliacao,
      }),
      ...(parte.pavimento_texto != undefined && {
        pavimento_texto: parte.pavimento_texto,
      }),
      ...(parte.torre_texto != undefined && {
        torre_texto: parte.torre_texto,
      }),
    };

    try {
      const response = await api.post("/api/unidade/", payloadCompleto);
      return response.data;
    } catch (error: any) {
      console.error("Erro detalhado ao criar parte:", error);
      console.error("Response data:", error.response?.data);
      console.error("Response status:", error.response?.status);
      console.error("Response headers:", error.response?.headers);
      throw new Error(
        `Erro ao criar parte do empreendimento: ${error.message}`
      );
    }
  }

  async listPartesEmpreendimento(
    empreendimentoId: number
  ): Promise<ParteEmpreendimento[]> {
    try {
      const response = await api.get("/api/unidade/", {
        params: { empreendimento: empreendimentoId },
      });

      // Filtrar novamente no frontend para garantir
      const partesFiltradas = response.data.results.filter(
        (parte: any) => parte.empreendimento === empreendimentoId
      );

      return partesFiltradas;
    } catch (error) {
      console.error("Erro ao listar partes:", error);
      throw error;
    }
  }

  async listCidades(): Promise<any[]> {
    const response = await api.get("/api/cidades/");
    return response.data.results;
  }

  /**
   * Baixa o CSV consolidado do empreendimento (dados cadastrais, unidades,
   * movimentações e consolidações/snapshots) e dispara o download no browser.
   *
   * `data_inicio` / `data_fim` (YYYY-MM-DD) limitam apenas as seções de
   * movimentações e consolidações.
   */
  async exportarCsv(
    id: number,
    params?: { data_inicio?: string; data_fim?: string }
  ): Promise<void> {
    const response = await api.get(`${this.baseUrl}${id}/exportar-csv/`, {
      params,
      responseType: "blob",
    });
    baixarBlob(response, `empreendimento-${id}.csv`);
  }

  /**
   * Baixa o CSV consolidado de TODOS os empreendimentos visíveis ao usuário.
   *
   * Aceita os mesmos filtros da listagem (search / incorporadora / cidade) —
   * o backend reaproveita o mesmo queryset — além do recorte de período.
   */
  async exportarCsvGeral(params?: {
    data_inicio?: string;
    data_fim?: string;
    search?: string;
    incorporadora?: number;
    cidade?: number;
  }): Promise<void> {
    const response = await api.get(`${this.baseUrl}exportar-csv-geral/`, {
      params,
      responseType: "blob",
      // O arquivo é montado em stream e pode demorar em bases grandes.
      timeout: 10 * 60 * 1000,
    });
    baixarBlob(response, "export-geral-tactimobi.csv");
  }
}

/**
 * Dispara o download de uma resposta blob, usando o filename que o backend
 * mandou no Content-Disposition (com fallback).
 */
function baixarBlob(response: AxiosResponse<Blob>, fallback: string): void {
  const disposition = response.headers["content-disposition"] as
    | string
    | undefined;
  const match = disposition?.match(/filename="?([^";]+)"?/);
  const filename = match?.[1] ?? fallback;

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export class TipoParteEmpreendimentoService {
  async getTipos(): Promise<TipoParteEmpreendimento[]> {
    const response = await api.get("/api/tipos-parte-empreendimento/");
    return response.data.results;
  }

  async getTipoById(id: number): Promise<TipoParteEmpreendimento> {
    const response = await api.get(`/api/tipos-parte-empreendimento/${id}/`);
    return response.data;
  }

  async addTipo(
    tipo: TipoParteEmpreendimentoCreate
  ): Promise<TipoParteEmpreendimento> {
    const response = await api.post("/api/tipos-parte-empreendimento/", tipo);
    return response.data;
  }

  async updateTipo(
    id: number,
    tipo: Partial<TipoParteEmpreendimentoCreate>
  ): Promise<TipoParteEmpreendimento> {
    const response = await api.put(
      `/api/tipos-parte-empreendimento/${id}/`,
      tipo
    );
    return response.data;
  }

  async deleteTipo(id: number): Promise<void> {
    await api.delete(`/api/tipos-parte-empreendimento/${id}/`);
  }
}

export class ItemEmpreendimentoService {
  async getItens(): Promise<ItemEmpreendimento[]> {
    const response = await api.get<PaginatedResponse<ItemEmpreendimento>>(
      "/api/item-empreendimento/"
    );
    return response.data.results;
  }

  async getItemById(id: string): Promise<ItemEmpreendimento | null> {
    try {
      const response = await api.get<ItemEmpreendimento>(
        `/api/item-empreendimento/${id}/`
      );
      return response.data;
    } catch (error) {
      console.error("Erro ao buscar item por ID:", error);
      return null;
    }
  }

  async addItem(item: ItemEmpreendimentoCreate): Promise<ItemEmpreendimento> {
    const response = await api.post<ItemEmpreendimento>(
      "/api/item-empreendimento/",
      item
    );
    return response.data;
  }

  async updateItem(
    id: string,
    item: Partial<ItemEmpreendimentoCreate>
  ): Promise<ItemEmpreendimento> {
    const response = await api.put<ItemEmpreendimento>(
      `/api/item-empreendimento/${id}/`,
      item
    );
    return response.data;
  }

  async deleteItem(id: string): Promise<boolean> {
    try {
      await api.delete(`/api/item-empreendimento/${id}/`);
      return true;
    } catch (error) {
      console.error("Erro ao deletar item:", error);
      return false;
    }
  }

  async getItensByCategoria(categoria: string): Promise<ItemEmpreendimento[]> {
    const response = await api.get<PaginatedResponse<ItemEmpreendimento>>(
      "/api/item-empreendimento/?tipo=" + categoria,
      {}
    );

    return response.data.results;
  }

  async getAllItens(): Promise<ItemEmpreendimento[]> {
    let url: string | null = "/api/item-empreendimento/?offset=0&limit=100";
    let allResults: ItemEmpreendimento[] = [];
    const visitadas = new Set<string>();

    while (url) {
      // Protege contra loop infinito caso a API repita o "next"
      if (visitadas.has(url)) break;
      visitadas.add(url);

      // Tipagem explícita do response
      const response: AxiosResponse<PaginatedResponse<ItemEmpreendimento>> =
        await api.get<PaginatedResponse<ItemEmpreendimento>>(url);

      allResults.push(...(response.data.results || []));

      // Atualiza a URL para a próxima página (relativa)
      url = toRelativeUrl(response.data.next);
    }

    return allResults;
  }
}

export const empreendimentoService = new EmpreendimentoService();

export const itemEmpreendimentoService = new ItemEmpreendimentoService();
